import React, { useState, useMemo, useEffect } from 'react';
import EmailCard from './EmailCard';
import TaskSummary from './TaskSummary';
import { TIER_CONFIG, TIER_ORDER, normalizeImportance } from '../utils/formatters';
import { starEmail, snoozeEmail } from '../utils/api';

export default function ResultsView({
  results,
  allResults,
  wasCancelled,
  totalRequested,
  filterLabel,
  snoozeLabelId,
  onPatchEmail,
  sessionMeta,
  onDismiss,
}) {
  const [activeTiers, setActiveTiers] = useState(new Set(TIER_ORDER));
  const [sortBy, setSortBy] = useState('importance');

  const resultsSignature = useMemo(
    () => results.map((r) => r._resultKey || r.email?.id).join('|'),
    [results]
  );

  useEffect(() => {
    setActiveTiers(new Set(TIER_ORDER));
  }, [resultsSignature]);

  const toggleTier = (tier) => {
    setActiveTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier);
      else next.add(tier);
      return next;
    });
  };

  const handleStar = async (messageId, starred) => {
    try {
      const out = await starEmail(messageId, starred);
      if (out.success && onPatchEmail) {
        onPatchEmail(messageId, { labels: out.labels });
      }
    } catch (e) {
      console.error('Failed to star email:', e);
    }
  };

  const handleSnooze = async (messageId, snoozed) => {
    try {
      const out = await snoozeEmail(messageId, snoozed);
      if (out.success && onPatchEmail) {
        onPatchEmail(messageId, { labels: out.labels });
      }
    } catch (e) {
      console.error('Failed to snooze email:', e);
    }
  };

  const tierCounts = useMemo(() => {
    const counts = { critical: 0, important: 0, routine: 0, low: 0 };
    results.forEach((r) => {
      const t = normalizeImportance(r.importance);
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [results]);

  const sorted = useMemo(() => {
    let filtered = results.filter((r) => activeTiers.has(normalizeImportance(r.importance)));
    if (sortBy === 'importance') {
      filtered.sort(
        (a, b) =>
          TIER_ORDER.indexOf(normalizeImportance(a.importance)) -
          TIER_ORDER.indexOf(normalizeImportance(b.importance))
      );
    } else if (sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.email.date) - new Date(a.email.date));
    } else if (sortBy === 'sender') {
      filtered.sort((a, b) => (a.email.sender || '').localeCompare(b.email.sender || ''));
    }
    return filtered;
  }, [results, activeTiers, sortBy]);

  const showTaskSummary = !filterLabel || filterLabel === 'all';

  return (
    <div>
      {showTaskSummary && <TaskSummary results={allResults || results} filter={filterLabel} />}

      {wasCancelled && (
        <div className="results-cancelled" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '1.2em' }}>⚠️</span> Processing was cancelled — {results.length} of{' '}
          {totalRequested} emails were analyzed.
        </div>
      )}

      <div className="results-header">
        <div className="results-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>📊</span> Analysis Results ({results.length} email{results.length !== 1 ? 's' : ''})
        </div>
        <select
          className="btn btn-sm"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{ background: 'var(--surface-2)', color: 'var(--text)' }}
        >
          <option value="importance">Sort by Importance</option>
          <option value="date">Sort by Date</option>
          <option value="sender">Sort by Sender</option>
        </select>
      </div>

      <div className="stats-bar">
        {TIER_ORDER.map((tier) => (
          <div key={tier} className="stat-card" onClick={() => toggleTier(tier)} style={{ cursor: 'pointer' }}>
            <div className="stat-num" style={{ color: TIER_CONFIG[tier].color }}>
              {tierCounts[tier]}
            </div>
            <div className="stat-label">{TIER_CONFIG[tier].label}</div>
          </div>
        ))}
      </div>

      <div className="tier-filters">
        {TIER_ORDER.map((tier) => {
          const Icon = TIER_CONFIG[tier].icon;
          return (
            <button
              key={tier}
              type="button"
              className={`tier-pill ${activeTiers.has(tier) ? 'active' : ''}`}
              data-tier={tier}
              onClick={() => toggleTier(tier)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Icon size={14} /> {TIER_CONFIG[tier].label} ({tierCounts[tier]})
            </button>
          );
        })}
      </div>

      <div className="email-grid">
        {sorted.map((result, idx) => (
          <EmailCard
            key={result._resultKey || result.email.id}
            result={result}
            index={idx}
            onStar={handleStar}
            onSnooze={handleSnooze}
            snoozeLabelId={snoozeLabelId}
            sessionMeta={sessionMeta}
            onDismiss={onDismiss}
          />
        ))}
        {sorted.length === 0 && (
          <div className="loading-center">
            <span>No emails match the current filters.</span>
          </div>
        )}
      </div>
    </div>
  );
}
